import { createGqlFetcher } from 'app/lib/gqlFetcher'
import { ArrowPathIcon } from '@heroicons/react/20/solid'
import { withZod } from '@remix-validated-form/with-zod'
import classNames from 'classnames'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { z } from 'zod'
import { AddNewIntegrationModal } from '../AddNewIntegrationModal'
import {
  ELEVEN_LABS_AGENTS_QUERY,
  ELEVEN_LABS_BOUND_LOCATIONS_QUERY,
  BIND_LOCATION_ELEVEN_LABS_AGENT,
  UNBIND_LOCATION_ELEVEN_LABS_AGENT,
} from '../graphql'
import { useIntegrationQuery, type IntegrationItem } from '../useIntegrationQuery'
import Button from 'app/components/Button'
import { useConfirmation } from 'app/components/ConfirmationContext'
import FormCombo from 'app/components/form/FormCombo'
import FormComboAsync from 'app/components/form/FormComboAsync'
import FormSelect from 'app/components/form/FormComboSelect'
import { SmartForm } from 'app/components/form/SmartForm'
import Spinner from 'app/components/Spinner'
import { gqlMutation } from 'app/lib/graphqlClient'
import { closeModal, showModal } from 'app/lib/modal'
import { notifyError, notifySuccess } from 'app/services/notify.client'
import type { LocationJson } from 'app/types/entities'
import { logError } from 'lib/logging'
import { OptionType } from 'types/OptionType'
import { useQuery } from 'urql'

type ElevenLabsAgent = IntegrationItem

type ElevenLabsAgentsQueryResult = {
  integrationOptions: {
    elevenLabsAgents: ElevenLabsAgent[]
  }
}

type BindElevenLabsAgentResult = {
  bindLocationElevenLabsAgent: { id: string; elevenLabsAgentId?: string | null }
}

type UnbindElevenLabsAgentResult = {
  unbindLocationElevenLabsAgent: { id: string }
}

const elevenLabsSchema = z.object({
  locationId: z.any().optional(),
  elevenLabsAgentId: z.any().optional(),
})
const elevenLabsValidator = withZod(elevenLabsSchema)

export function ElevenLabsFormModal({
  onAdd,
  locationId,
  locationName,
  elevenLabsAgentId,
}: {
  onAdd: () => void
  locationId?: string
  locationName?: string
  elevenLabsAgentId?: string
}) {
  const isEditing = !!locationId
  const [selectedLocation, setSelectedLocation] = useState<OptionType | undefined>(
    locationId ? { id: locationId, name: locationName || ``, value: locationId } : undefined,
  )
  const [selectedAgent, setSelectedAgent] = useState<OptionType | undefined>()
  const [saving, setSaving] = useState(false)
  const [removing, setRemoving] = useState(false)
  const [confirm] = useConfirmation()
  const [{ data: boundLocationsData, fetching: fetchingBoundLocations, error: boundLocationsError }] = useQuery({
    query: ELEVEN_LABS_BOUND_LOCATIONS_QUERY,
    pause: isEditing,
    requestPolicy: `network-only`,
  })

  const boundLocationIds = useMemo(
    () =>
      new Set(
        boundLocationsData?.locations.hits
          .filter((location) => location.elevenLabsAgentId)
          .map((location) => location.id) ?? [],
      ),
    [boundLocationsData],
  )

  useEffect(() => {
    if (boundLocationsError) logError(`Failed to load locations with ElevenLabs bindings`, boundLocationsError)
  }, [boundLocationsError])

  const locationOptionsMapper = useCallback(
    (res: { hits?: LocationJson[] }): OptionType[] =>
      (res?.hits ?? []).map((hit: LocationJson) => ({
        id: hit.id || hit.name || ``,
        name: hit.name || ``,
        value: hit.id || ``,
        meta: {
          isUsed: boundLocationIds.has(hit.id!),
          tooltipForDisabledOption: `This location is already connected to ElevenLabs`,
        },
      })),
    [boundLocationIds],
  )

  const { options, fetching, error, refreshing, refresh } = useIntegrationQuery<
    ElevenLabsAgentsQueryResult,
    ElevenLabsAgent
  >({
    query: ELEVEN_LABS_AGENTS_QUERY,
    extractItems: (data) => data.integrationOptions.elevenLabsAgents,
    mapToOption: (agent) => ({
      id: agent.id,
      name: agent.name,
      value: agent.id,
      meta: {
        isUsed: agent.isUsed,
        tooltipForDisabledOption: `This agent is connected to another location`,
      },
    }),
    errorMessage: `Failed to load ElevenLabs agents`,
  })

  // When editing, preselect the currently-bound agent once options load.
  useEffect(() => {
    if (options.length && elevenLabsAgentId && !selectedAgent) {
      const opt = options.find((o) => o.id === elevenLabsAgentId)
      if (opt) setSelectedAgent(opt)
    }
  }, [options, elevenLabsAgentId, selectedAgent])

  const effectiveLocationId = locationId || (selectedLocation?.value as string | undefined)

  const goBackToSelectIntegrationModal = () => {
    closeModal()
    showModal(
      <AddNewIntegrationModal onIntegrationAdded={onAdd} />,
      {
        title: `Add New Integration`,
        isFullScreen: false,
        overflowHidden: false,
        removePadding: true,
        useRadix: true,
      },
      false,
    )
  }

  const onSave = async () => {
    if (!effectiveLocationId || !selectedAgent?.value) return
    const nextAgentId = selectedAgent.value as string
    // No-op if the binding is unchanged when editing.
    if (isEditing && elevenLabsAgentId === nextAgentId) {
      closeModal()
      return
    }
    setSaving(true)
    try {
      // Re-point: the backend enforces a strict 1:1 and rejects binding an
      // already-bound location, so an existing binding must be cleared first.
      if (isEditing && elevenLabsAgentId) {
        const { error: unbindError } = await gqlMutation(UNBIND_LOCATION_ELEVEN_LABS_AGENT, {
          locationId: effectiveLocationId,
        })
        if (unbindError) {
          notifyError(`Failed to update agent: ${unbindError.message}`)
          return
        }
      }
      const { error: mutationError } = await gqlMutation(BIND_LOCATION_ELEVEN_LABS_AGENT, {
        locationId: effectiveLocationId,
        elevenLabsAgentId: nextAgentId,
      })
      if (mutationError) {
        // Rollback: restore previous binding if unbind succeeded but bind failed
        if (isEditing && elevenLabsAgentId) {
          const { error: rollbackError } = await gqlMutation(BIND_LOCATION_ELEVEN_LABS_AGENT, {
            locationId: effectiveLocationId,
            elevenLabsAgentId,
          })
          if (rollbackError) {
            notifyError(`Failed to connect agent and could not restore previous binding. Please reconnect manually.`)
          } else {
            notifyError(`Failed to connect new agent: ${mutationError.message}. Previous agent was restored.`)
          }
        } else {
          notifyError(`Failed to connect agent: ${mutationError.message}`)
        }
        return
      }
      notifySuccess(`ElevenLabs agent connected`)
      onAdd()
      closeModal()
    } finally {
      setSaving(false)
    }
  }

  const onDisconnect = () => {
    if (!effectiveLocationId) return
    return confirm({
      title: `Disconnect ElevenLabs Agent`,
      description: `This will unbind the ElevenLabs agent from this location. The agent itself is not changed on ElevenLabs.`,
      variant: `danger`,
    }).then(async () => {
      setRemoving(true)
      try {
        const { error: mutationError } = await gqlMutation(UNBIND_LOCATION_ELEVEN_LABS_AGENT, {
          locationId: effectiveLocationId,
        })
        if (mutationError) {
          notifyError(`Failed to disconnect agent: ${mutationError.message}`)
          return
        }
        notifySuccess(`ElevenLabs agent disconnected`)
        onAdd()
        closeModal()
      } finally {
        setRemoving(false)
      }
    })
  }

  const busy = saving || removing

  return (
    <>
      <SmartForm
        id="elevenlabs-integration-form"
        className="!mt-0 w-[600px] grow-0 justify-start !gap-y-4 px-6 py-4"
        validator={elevenLabsValidator}
        hideButtons
        preventSubmit
        onSubmit={() => onSave()}
      >
        {isEditing ? (
          <FormSelect
            cols={5}
            name="locationId"
            label="Trackable Location"
            options={[{ label: locationName || ``, value: locationId! }]}
            value={locationId}
            disabled
            labelClassName="text-body-sm text-strong"
          />
        ) : fetchingBoundLocations ? (
          <div className="col-span-5 flex items-center pt-5">
            <Spinner size={5} />
          </div>
        ) : boundLocationsError ? (
          <div className="col-span-5 pt-5 text-sm text-red-600">Failed to load locations</div>
        ) : (
          <FormComboAsync
            cols={5}
            name="locationId"
            required={true}
            showSearchInDropdown={true}
            label="Trackable Location"
            value={selectedLocation}
            optionsMapper={locationOptionsMapper}
            fetcher={createGqlFetcher('locations')}
            labelClassName="text-body-sm text-strong"
            onChange={(val) => setSelectedLocation(val)}
            newLineError={false}
            showErrorIcon
          />
        )}

        {fetching ? (
          <div className="col-span-5 flex items-center pt-5">
            <Spinner size={5} />
          </div>
        ) : error ? (
          <div className="col-span-5 pt-5 text-sm text-red-600">Failed to load agents</div>
        ) : options.length === 0 ? (
          <div className="col-span-5 pt-5 text-sm text-subtle">No agents available</div>
        ) : (
          <FormCombo
            cols={5}
            name="elevenLabsAgentId"
            label="ElevenLabs Agent"
            value={selectedAgent}
            options={options}
            labelClassName="text-body-sm text-strong"
            onChange={(opt) => setSelectedAgent(opt)}
            showSearchInDropdown={true}
            newLineError={false}
            showErrorIcon
          />
        )}
        <div className="col-span-1 flex items-center justify-center pt-5">
          <ArrowPathIcon
            className={classNames(`ml-2 h-5 w-5 cursor-pointer`, refreshing && `animate-spin`)}
            onClick={refresh}
          />
        </div>
      </SmartForm>

      <div className="sticky bottom-0 w-full rounded-b-xl border-t border-default bg-surface-raised px-6 py-3">
        <div className="flex items-center justify-between">
          <div>
            {isEditing && elevenLabsAgentId ? (
              <Button
                label="Disconnect"
                mode="danger"
                size="md"
                onClick={() => onDisconnect()}
                loading={removing}
                disabled={busy}
              />
            ) : null}
          </div>
          <div className="flex gap-2">
            {isEditing ? (
              <Button label="Cancel" mode="white" size="md" onClick={() => closeModal()} disabled={busy} />
            ) : (
              <Button
                label="Back"
                mode="white"
                size="md"
                onClick={() => goBackToSelectIntegrationModal()}
                disabled={busy}
              />
            )}
            <Button
              type="submit"
              form="elevenlabs-integration-form"
              label="Save"
              mode="primary"
              size="md"
              loading={saving}
              disabled={
                busy || fetchingBoundLocations || !!boundLocationsError || !effectiveLocationId || !selectedAgent
              }
            />
          </div>
        </div>
      </div>
    </>
  )
}
